import type { QuizAttemptPublic, QuizPublic } from '@repo/types/quiz';

import { isAfter, isWithinInterval } from 'date-fns';
import { CheckCheckIcon, CheckCircle2Icon, CircleXIcon, PencilLineIcon } from 'lucide-react';
import { Link } from 'react-router';

import QuizBasicStats from '@/components/features/quizzes/QuizBasicStats';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

interface QuizManageHeaderProps {
  quiz: QuizPublic & { quizresps: QuizAttemptPublic[] };
}

export default function QuizManageHeader(quizDetailHeaderProps: QuizManageHeaderProps) {
  const { quiz } = quizDetailHeaderProps;

  return (
    <div className="bg-background flex w-full flex-col gap-4 rounded-2xl border p-4 md:gap-2">
      <div className="flex w-full flex-row items-center gap-4">
        <AvailabilityBadge available={quiz.available} deadline={quiz.deadline} />
      </div>
      <div className="space-y-1">
        <h1 className="text-center text-xl font-bold md:text-start">{quiz.name}</h1>
        <p className="text-muted-foreground hidden text-sm md:block">{quiz.desc}</p>
      </div>
      <div className="flex flex-col gap-4 text-xs text-nowrap md:flex-row md:items-end">
        <QuizBasicStats quiz={quiz} className="md:flex md:flex-row md:flex-wrap md:[&>div]:items-start" />
        <p className="text-muted-foreground block text-center md:hidden">{quiz.desc}</p>

        <Button className={'w-full md:ml-auto md:w-fit'} variant={'outline'} asChild>
          <Link to={`/manage/quizzes/builder/${quiz.id}/basic-info`}>
            <PencilLineIcon /> Edytuj
          </Link>
        </Button>
      </div>
    </div>
  );
}

interface AvailabilityBadgeProps {
  available: string | null;
  deadline: string | null;
}

function AvailabilityBadge(availabilityBadgeProps: AvailabilityBadgeProps) {
  const { available, deadline } = availabilityBadgeProps;
  if (
    isWithinInterval(new Date(), {
      start: available || new Date('1000-01-01'),
      end: deadline || new Date('9999-12-31'),
    })
  ) {
    return (
      <Badge variant={'success'} className="mx-auto md:mx-0">
        <CheckCircle2Icon /> Aktywny
      </Badge>
    );
  }

  if (deadline && isAfter(new Date(), deadline)) {
    return (
      <Badge variant={'destructive'} className="mx-auto md:mx-0">
        <CheckCheckIcon /> Ukończony
      </Badge>
    );
  }

  return (
    <Badge variant={'warning'} className="mx-auto md:mx-0">
      <CircleXIcon /> Nie rozpoczęty
    </Badge>
  );
}
